import {
  Card,
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';

import Iconify from '../../components/Iconify';
import useToggle from '../../hooks/useToggle';

const RootStyle = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(5),
  },
}));

const CardStyle = styled(Card)(({ theme, url }) => ({
  border: 0,
  width: '100%',
  minHeight: 360,
  maxHeight: 360,
  margin: 'auto',
  position: 'relative',
  backgroundImage: `url(/assets/clearoverlay.svg), url(${url})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  outline: '2px solid transparent',
  outlineOffset: -2,
  transition: 'box-shadow 0.2s ease, transform 0.2s ease, outline-color 0.2s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2)',
    transform: 'translateY(-2px)',
    outlineColor: theme.palette.primary.main,
  },
}));

const RAISONS = [
  {
    image: '/assets/background/AdobeStock_305621580.jpg',
    title: 'Un port en eau profonde',
    paragraphs: [
      "Premier port d’Afrique de l’Ouest et d’Afrique centrale, fonctionnant 24 h/24, il dessert l’hinterland (Burkina Faso, Niger, Mali) et offre des liaisons rapides : 7 jours vers l’Europe, 13 jours vers les États-Unis et 29 jours vers la Chine.",
      'Grâce à sa position clé, à la qualité de ses services et à ses activités de transbordement, il est un hub régional important.',
    ],
  },
  {
    image: '/assets/background/Salt-Lomé-Togo.jpg',
    title: 'Un hub aérien performant',
    points: [
      'Lomé est un hub commercial et économique.',
      'L’Aéroport International Gnassingbé Eyadéma (AIGE) accueille jusqu’à 2 millions de passagers par an.',
      'Vols directs : Paris, Bruxelles, New York, Washington et Addis-Abeba.',
      'Dessert les capitales d’Afrique de l’Ouest et centrale.',
      'Hub principal pour la compagnie ASKY.',
    ],
  },
  {
    image: '/assets/background/image_4.jpg',
    title: "Une porte d'entrée stratégique",
    points: [
      'Accès maritime essentiel pour les pays enclavés de l’hinterland et accès à un marché régional de près de 400 millions de personnes via l’UEMOA et la CEDEAO.',
      'Intégration régionale facilitant la libre circulation des biens, services et personnes, avec une harmonisation du droit des affaires sous l’OHADA pour une pratique commerciale uniforme.',
      'Exemption de visa d’entrée pour les Africains pour 30 jours.',
    ],
  },
  {
    image: '/assets/background/istockphoto-1171807822.jpg',
    title: 'Un climat des affaires attractif',
    points: [
      'Classé 1er en Afrique de l’Ouest et 3e en Afrique subsaharienne pour la facilité de faire des affaires dans le rapport Business Ready 2025 de la Banque mondiale (BM), grâce à des réformes significatives.',
      'Stabilité économique reconnue par les agences de notation financière, avec des guichets uniques pour simplifier les opérations portuaires, l’accès au foncier, la création d’entreprises et l’investissement.',
    ],
  },
  {
    image: '/assets/background/AdobeStock_191995730.jpg',
    title: 'Un accès privilégié au marché mondial',
    points: [
      'Accès privilégié au marché de l’Union européenne grâce à l’appartenance à l’ACP-UE et à l’accord TSA.',
      'Détention d’une licence textile depuis 2017, facilitant l’exportation de produits textiles vers l’Union européenne et les États-Unis.',
      'Avantages des accords AGOA permettant au Togo d’exporter vers les États-Unis sans quota ni droits de douane.',
      'Positionnement stratégique comme partenaire commercial pour l’accès au marché chinois grâce à des accords bilatéraux avec la Chine.',
    ],
  },
  {
    image: '/assets/background/photo-lawyers.jpg',
    title: 'Un cadre juridique et fiscal attractif',
    points: [
      'Régime douanier de droit commun avec un code général des impôts modernisé.',
      'Code des investissements de 2019 offrant des réductions et des exonérations fiscales pour les investissements dépassant 50 millions FCFA.',
      'Régime de zone franche offrant des avantages pour les entreprises exportant au moins 70 % de leur production.',
      'Régime de zone franche spécifique pour les industries du textile.',
    ],
  },
  {
    image: '/assets/background/AdobeStock_137648639.jpg',
    title: 'Une place financière de choix',
    points: [
      'Lomé, la capitale du Togo, est le siège de la Banque Ouest Africaine de Développement (BOAD).',
      'La Banque d’Investissement et de Développement de la CEDEAO (BIDC) est aussi basée à Lomé.',
      'Des groupes bancaires panafricains importants, comme Ecobank et Oragroup, ont leur siège dans cette ville.',
    ],
  },
  {
    image: '/assets/background/AdobeStock_144613163.jpg',
    title: 'Un gouvernement engagé',
    points: [
      'Le Togo privilégie les partenariats public-privé pour sa croissance économique.',
      'Mise en place de plans de développement qui seront financés à 65 % par le privé.',
      'Le gouvernement écoute et soutient les besoins des partenaires privés pour stimuler les investissements.',
      'Le Ministère délégué chargé de la Promotion des Investissements, de l’Industrie et de la Souveraineté économique est l’interface pour aider les investisseurs dans leurs projets d’installation.',
    ],
  },
  {
    image: '/assets/background/pays_paix.jpg',
    title: 'Un pays de paix et de stabilité',
    points: [
      'Stabilité politique notable au Togo.',
      'Sécurité des investissements et des capitaux.',
      'Eaux territoriales parmi les plus sûres de l’Afrique de l’Ouest.',
    ],
  },
  {
    image: '/assets/background/AdobeStock_411430356.jpg',
    title: 'Une main-d’œuvre jeune et dynamique',
    points: [
      'Population jeune : 70 % des Togolais ont moins de 35 ans.',
      'Taux d’alphabétisation élevé : 85 %.',
      'Main-d’œuvre abondante et qualifiée, prête pour la formation et l’emploi diversifié.',
      'Un système éducatif innovant avec la mise en avant de la technologie.',
    ],
  },
];

export default function HomeRaison() {
  return (
    <Stack width={1} position="relative">
      <RootStyle>
        <Grid container spacing={{ xs: 2, sm: 5, md: 8 }} justifyContent="center">
          {RAISONS.map((raison, index) => (
            <Grid key={raison.title} item xs={12} md={4}>
              <RaisonCard
                number={String(index + 1).padStart(2, '0')}
                title={raison.title}
                paragraphs={raison.paragraphs}
                points={raison.points}
                image={raison.image}
              />
            </Grid>
          ))}
        </Grid>
      </RootStyle>
    </Stack>
  );
}

const RaisonCard = ({ number, title, paragraphs = [], points = [], image }) => {
  const { toggle, onOpen, onClose } = useToggle();

  return (
    <CardStyle url={image}>
      <Stack
        onClick={onOpen}
        height={360}
        justifyContent="center"
        sx={{ cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.18)' }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            minWidth: 48,
            px: 1.5,
            py: 1,
            color: 'grey.900',
            textAlign: 'center',
            bgcolor: 'warning.main',
            borderRadius: 1,
          }}
        >
          {number}
        </Typography>

        <Stack
          justifyContent="center"
          minHeight={120}
          px={3}
          py={2.5}
          sx={{ backgroundColor: 'rgba(0, 0, 0, 0.68)' }}
        >
          <Typography variant="h4" color="common.white" fontWeight="bold" textAlign="center">
            {title}
          </Typography>
        </Stack>
      </Stack>
      {toggle && (
        <Dialog open={toggle} onClose={onClose}>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="start" pb={3}>
              <Typography variant="h4">{title}</Typography>
              <IconButton onClick={onClose}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              {paragraphs.map((paragraph) => (
                <DialogContentText key={paragraph} textAlign="justify">
                  {paragraph}
                </DialogContentText>
              ))}
              {points.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                  {points.map((point) => (
                    <Typography component="li" key={point} color="text.secondary" textAlign="justify" sx={{ mb: 1 }}>
                      {point}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </CardStyle>
  );
};
